import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { BooleanParam, createEnumParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import EmployerSelect from "@components/fields/EmployerSelect";
import TimePeriodField from "@components/fields/TimePeriodField";
import SearchInput from "@components/fieldsNative/SearchInput";
import styles from "@components/forms/AdminJourneyFilters.module.css";
import Form from "@components/forms/Form";
import { TimePeriodEnum } from "@typing/Enums";
import { onChangeWithPaid } from "@utils/formUtils";

type FormData = {
  employerId?: string | null;
  endDate?: string | null;
  searchTerm?: string | null;
  selfPaid?: boolean | null;
  startDate?: string | null;
  timePeriod?: TimePeriodEnum | null;
};

type Props = {
  filters: FormData;
  onChange: (filterValues: FormData) => void;
};

const JourneyTagFilters = ({ filters, onChange }: Props) => {
  const intl = useIntl();

  const timePeriodParam = createEnumParam<TimePeriodEnum>(Object.values(TimePeriodEnum));

  const [query, setQuery] = useQueryParams({
    employerId: withDefault({ ...StringParam, urlName: "journeyTagEmployerId" }, filters.employerId),
    employerName: { ...StringParam, urlName: "journeyTagEmployerName" },
    endDate: withDefault({ ...StringParam, urlName: "journeyTagEndDate" }, filters.endDate),
    searchTerm: withDefault({ ...StringParam, urlName: "journeyTagSearch" }, filters.searchTerm),
    selfPaid: withDefault({ ...BooleanParam, urlName: "journeyTagSelfPaid" }, filters.selfPaid),
    startDate: withDefault({ ...StringParam, urlName: "journeyTagStartDate" }, filters.startDate),
    timePeriod: withDefault({ ...timePeriodParam, urlName: "journeyTagTimePeriod" }, filters.timePeriod)
  });

  const defaultValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const employerId = form.watch("employerId");
  const endDate = form.watch("endDate");
  const searchTerm = form.watch("searchTerm");
  const selfPaid = form.watch("selfPaid");
  const startDate = form.watch("startDate");
  const timePeriod = form.watch("timePeriod");

  useEffect(() => {
    const values = { employerId, endDate, searchTerm, selfPaid, startDate, timePeriod };
    if (!equal(values, filters)) {
      setQuery(values);
      onChangeWithPaid(onChange, values);
    }
  }, [employerId, endDate, filters, form, onChange, searchTerm, selfPaid, setQuery, startDate, timePeriod]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol>
            <EmployerSelect<FormData>
              allowAny
              control={form.control}
              error={form.formState.errors.employerId}
              initialOption={{ label: query.employerName ?? "", value: query.employerId ?? "" }}
              label={intl.formatMessage({ id: "forms.journeyTagFilters.employerLabel" })}
              name="employerId"
              onSelection={(option: { label: string }) => {
                setQuery({ ...query, ...{ employerName: option.label } });
              }}
            />
          </IonCol>
          <TimePeriodField form={form} />
          <IonCol>
            <SearchInput
              error={form.formState.errors.searchTerm?.message}
              initialValue={defaultValues.searchTerm}
              name="searchTerm"
              placeholder={intl.formatMessage({ id: "forms.journeyTagFilters.searchLabel" })}
              setValue={v => form.setValue("searchTerm", v)}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default JourneyTagFilters;
